// @ts-nocheck

import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import { getSportIdFromProps } from '../../selectors/_propsGetters_';
import { selectEntitiesFavoritesMatchCount } from '../../selectors/entities/favorites/entitiesFavoritesPrimary.selector';
import { selectEntitiesSportsEntitySportName } from '../../selectors/entities/sports/entitiesSportsPrimary.selector';
import { selectMenu } from '../../selectors/menu/menuPrimary.selector';
import {
  SportsMenuSportIdsWithCompetitions,
  SportsMenuSportIdsWithCountryTitle,
  getSportsMenuTitle,
  selectSportMenuSportBySportIds,
  selectSportsMenuMainTopCompetitions,
  selectSportsMenuTournamentsBySportId,
} from '../../selectors/sportsMenu.selector';
import { selectUserConnected, selectUserDidReceivedUserData } from '../../selectors/user/userPrimary.selector';
import { type Menu } from '../../states/menu';
import { SportListItemTypes, type SportsMenuItem } from '../../states/sportsmenu';
import { type CategoryId } from '../../types/categories';
import { CategoryIds } from '../../types/categories';
import { type SelectorMaker, type Selector } from '../../types/selectors';
import { SportIds } from '../../types/sports';
import { createShallowEqualSelector } from '../../utils/reselect';

type CategoriesRowsBySportIdType = {
  [categoryId: number]: {
    order: number;
    categoryId: CategoryId;
    label: string;
    flagId?: string;
    content: Array<SportsMenuItem>;
  };
};

type SportsConfigMobileType = {
  header: string;
  topCompetitions: Array<SportsMenuItem>;
  content: Array<SportsMenuItem> | null;
};

type MobileSportsMenuPropsType = {
  config: SportsConfigMobileType;
  sportName?: string;
  nbFavorites: number;
  connected: boolean;
  menu: Menu;
  didReceivedUserData: boolean;
};

const makeMobileSportsMenuPropsSelector: SelectorMaker<MobileSportsMenuPropsType> = () => {
  const selectMainSportItems: Selector<Array<SportsMenuItem>> = createSelector(
    [selectSportMenuSportBySportIds],
    (sports) =>
      sports?.reduce((acc, sport) => {
        const { sportId } = sport ?? {};
        if (sport) {
          return [
            ...acc,
            {
              id: `row-s${sportId}`,
              type: SportListItemTypes.ROW,
              sportId,
              label: sport.sportName,
              to: sportId === SportIds.SPECIAL_ODDS ? `/sports/${sportId}` : `/menu/${sportId}`,
            },
          ];
        }
        return acc;
      }, []),
  );

  const selectCategoriesRowsBySportId: Selector<CategoriesRowsBySportIdType | undefined> = createSelector(
    [selectSportsMenuTournamentsBySportId],
    (tournaments) => {
      if (tournaments?.length) {
        const contentsByCategory = {};

        for (const tournament of tournaments) {
          const { sportId, categoryId, tournamentId, categoryName, categoryFlagId } = tournament;
          const sportIsNotWithCompetition = SportsMenuSportIdsWithCompetitions.indexOf(Number(sportId)) === -1;

          if (!contentsByCategory[categoryId]) {
            contentsByCategory[categoryId] = {
              order: Object.keys(contentsByCategory || []).length,
              categoryId,
              label: categoryName,
              flagId: categoryFlagId,
            };

            contentsByCategory[categoryId].content = [];

            const nbTournaments = tournaments.filter((t) => t.categoryId === categoryId).length;

            if (nbTournaments > 1 && sportIsNotWithCompetition) {
              contentsByCategory[categoryId].content?.push({
                id: `see-all-s${sportId}-c${categoryId}`,
                type: SportListItemTypes.SEE_ALL,
                label: __('See all'),
                to: `/sports/${sportId}/${categoryId}`,
              });
            }
          }

          contentsByCategory[categoryId].content?.push({
            id: `row-s${sportId}-c${categoryId}-t${tournamentId}`,
            type: SportListItemTypes.ROW,
            expandId: `sport:${sportId}-category:${categoryId}-tournament:${tournamentId}`,
            label: tournament.tournamentName,
            flagId: tournament.flag,
            to: `/sports/${sportId}/${categoryId}/${tournamentId}`,
            categoryId,
          });
        }

        return contentsByCategory;
      }
    },
  );

  const selectSportItemsBySportId: Selector<Array<SportsMenuItem>> = createSelector(
    [selectCategoriesRowsBySportId, getSportIdFromProps],
    (categoriesRowsBySport, sportId) => {
      if (sportId && categoriesRowsBySport) {
        const sportIsWithCompetition = SportsMenuSportIdsWithCompetitions.indexOf(Number(sportId)) !== -1;
        const sortedCategories = Object.keys(categoriesRowsBySport)
          .map((categoriesKey) => categoriesRowsBySport[categoriesKey])
          .sort((a, b) => a.order - b.order);

        let items = [];
        if (sportIsWithCompetition) {
          const competitions = {
            id: `competition-s${sportId}`,
            type: SportListItemTypes.COMPETITIONS,
            flagId: sortedCategories[0]?.flagId,
            label: undefined,
            content: [],
          };
          sortedCategories?.forEach((c) => {
            if (c?.content?.length) {
              competitions.content.push(...c.content);
            }
          });
          items = [competitions];
        } else {
          items = sortedCategories.map((category) => {
            const { label, flagId, content, categoryId } = category;

            let expandable = true;
            let to = null;
            const showNewTag = false;

            if (categoryId === CategoryIds.MULTIFOOT) {
              expandable = false;
              to = `/sports/${sportId}/${categoryId}`;
            }

            return {
              id: `row-s${sportId}-c${categoryId}`,
              expandId: `sport:${sportId}-category:${categoryId}`,
              expandable,
              to,
              type: SportListItemTypes.ROW,
              label,
              flagId,
              showNewTag,
              content,
            };
          });
        }

        // Pour les sports ou il y a des pays, trier par ordre alphabétique
        // https://gitlab.dev.winamax.fr/betting-clients/winabet/-/issues/432
        if (SportsMenuSportIdsWithCountryTitle.indexOf(parseInt(sportId)) !== -1) {
          items = items.sort((a, b) => a?.label?.localeCompare(b?.label) ?? 0);
        }

        return items;
      }
    },
  );

  const selectSportConfigContent: Selector<Array<SportsMenuItem>> = createSelector(
    [selectSportItemsBySportId, selectMainSportItems],
    (itemsBySportId, mainSportItems) => itemsBySportId ?? mainSportItems,
  );

  const selectSportsConfigMobile: Selector<SportsConfigMobileType> = createShallowEqualSelector(
    [selectSportsMenuMainTopCompetitions, selectSportConfigContent, getSportIdFromProps],
    (topCompetitions, content, sportId) => ({
      topCompetitions,
      header: getSportsMenuTitle(sportId),
      content,
    }),
  );

  return createSelector(
    [
      selectSportsConfigMobile,
      selectEntitiesSportsEntitySportName,
      selectEntitiesFavoritesMatchCount,
      selectUserConnected,
      selectMenu,
      selectUserDidReceivedUserData,
    ],
    (config, sportName, nbFavorites, connected, menu, didReceivedUserData) => ({
      config,
      sportName,
      nbFavorites,
      connected,
      menu,
      didReceivedUserData,
    }),
  );
};

export default makeMobileSportsMenuPropsSelector;
